import React, { useState } from 'react';
import { Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { BaseChart, ratio, pitchSubTypeColor, pitchTypeDisplayName } from 'yankees-react-utils';
import ReportCard from '../../../../components/ReportCard';

const getSeries = (pitches, isStartingPitcher, repertoire, rollingAverage, translate) => {
    const gamesMap = new Map();

    pitches.forEach((p) => {
        if (gamesMap.has(p.gamedate)) {
            gamesMap.get(p.gamedate).push({ fxtype: p.fxtype, fxsubtype: p.fxsubtype });
        } else {
            gamesMap.set(p.gamedate, [{ fxtype: p.fxtype, fxsubtype: p.fxsubtype }]);
        }
    });

    const allGameDates = [...gamesMap.keys()]
        .sort((a, b) => b.localeCompare(a));

    const gameDates = allGameDates
        .slice(0, isStartingPitcher ? 10 : 20)
        .sort((a, b) => a.localeCompare(b));

    const series = repertoire.filter((r) => r.fxtype !== 1).map((r) => ({
        name: pitchTypeDisplayName(r, true, true, translate),
        color: pitchSubTypeColor(r),
        marker: {
            symbol: 'circle',
        },
        data: gameDates.map((gd, index) => {
            const gameDateIndex = allGameDates.indexOf(gd);
            const previousGameDates = allGameDates.slice(gameDateIndex, gameDateIndex + rollingAverage);
            const { numerator, denominator } = previousGameDates.reduce((all, pgd) => {
                const gamePitches = gamesMap.get(pgd);
                return {
                    numerator: all.numerator + gamePitches.filter((gp) => gp.fxtype === r.fxtype && gp.fxsubtype === r.fxsubtype).length,
                    denominator: all.denominator + gamePitches.filter((x) => x.fxtype !== 1).length,
                };
            }, { numerator: 0, denominator: 0 });
            return {
                x: index,
                y: ratio(numerator, denominator) * 100,
            };
        }),
        type: 'spline',
    }));

    return { series, gameDates };
};
const TrendsChart = ({ pitches, isStartingPitcher, repertoire }) => {
    const { t: translate } = useTranslation();

    const rollingAverageOptions = isStartingPitcher ? [3, 5, 10] : [5, 10, 20];

    const [rollingAverage, setRollingAverage] = useState(rollingAverageOptions[1]);

    const { series, gameDates } = getSeries(pitches, isStartingPitcher, repertoire, rollingAverage, translate);
    const chartConfig = {
        chart: {
            width: 775,
            height: 300,
        },
        title: '',
        series,
        xAxis: {
            labels: {
                formatter() {
                    return translate('format.datetime', {
                    // eslint-disable-next-line react/no-this-in-sfc
                        val: new Date(gameDates[this.pos]),
                        interpolation: { escapeValue: false },
                        formatParams: { val: { month: 'numeric', day: 'numeric' } },
                    });
                },
            },
            tickPositions: Object.keys(gameDates).map((x) => +x),
        },
        yAxis: {
            labels: {
                format: '{value}%',
            },
            title: {
                text: '',
            },
        },
        tooltip: {
            crosshairs: true,
            shared: true,
            headerFormat: '',
            pointFormat: '{series.name}: {point.y:.0f}%<br />',
        },
    };
    return (
        <ReportCard title={translate('pitcher-overview.sections.trends')}>
            {pitches.length
                ? (
                    <>
                        <div className="d-flex flex-column align-items-center">
                            <div className="d-flex justify-content-end align-items-center mb-1 d-print-none" style={{ width: 775 }}>
                                <label>{translate('pitcher-overview.sections.trends-rolling-average-label')}&nbsp;</label>
                                <Form.Select
                                    size="sm"
                                    style={{ width: 75 }}
                                    onChange={(e) => setRollingAverage(+e.target.value)}
                                    value={rollingAverage}
                                >
                                    {rollingAverageOptions.map((rao) => <option key={rao} value={rao}>{rao}</option>)}
                                </Form.Select>
                            </div>
                            <BaseChart options={chartConfig} />
                        </div>
                    </>
                )
                : <div className="d-flex justify-content-center py-5 text-muted">{translate('pitcher-overview.no-data')}</div>}
        </ReportCard>
    );
};

export default TrendsChart;
